import { createInitialState, type GameState } from './gameState';
import { spendEnergy } from '../sim/energy';
import { seasonOf } from '../sim/seasons';
import { addGameMinutes } from '../sim/time';
import { CONFIG, type EnergyAction } from '../data/config';

type Listener = (state: GameState) => void;

/**
 * The one store all scenes read/write through — never ad-hoc globals.
 * Holds the single GameState and a tiny pub/sub so the UI scene can react.
 */
class GameStore {
  private state: GameState = createInitialState();
  private listeners = new Set<Listener>();
  /** When on, energy never runs out — a gentle mode for little players. */
  toddlerMode = ((): boolean => {
    try {
      return localStorage.getItem('sandyhollow.toddler') === '1';
    } catch {
      return false;
    }
  })();

  get(): GameState {
    return this.state;
  }

  /** Replace the whole state (e.g. after loading a save or starting new). */
  replace(state: GameState): void {
    this.state = state;
    this.emit();
  }

  /** Mutate in place via a callback, then notify listeners. */
  update(mutator: (state: GameState) => void): void {
    mutator(this.state);
    this.emit();
  }

  /** Notify listeners without a mutation (e.g. after an external batch change). */
  touch(): void {
    this.emit();
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private emit(): void {
    for (const l of this.listeners) l(this.state);
  }

  // ---- inventory helpers (scenes go through these, not raw arrays) ----

  countItem(itemId: string): number {
    let n = 0;
    for (const slot of this.state.inventory) if (slot.itemId === itemId) n += slot.qty;
    return n;
  }

  addItem(itemId: string, qty = 1): void {
    this.update((s) => {
      const slot = s.inventory.find((x) => x.itemId === itemId);
      if (slot) slot.qty += qty;
      else s.inventory.push({ itemId, qty });
      s.collected[itemId] = (s.collected[itemId] ?? 0) + qty; // collection log
    });
  }

  /** Spend energy for an action if affordable; false if too tired (any scene can call). */
  trySpendEnergy(action: EnergyAction): boolean {
    if (this.toddlerMode) return true; // infinite energy for little players
    const winter = seasonOf(this.state.clock.day).id === 'winter'; // the cold tires you out faster
    const r = spendEnergy(this.state.energy, action, winter ? 1.35 : 1);
    if (!r.ok) return false;
    const spent = this.state.energy - r.energy;
    this.update((s) => {
      s.energy = r.energy;
      s.clock = addGameMinutes(s.clock, Math.round(spent * CONFIG.gameMinutesPerEnergy)); // effort advances the day
    });
    return true;
  }

  /** Restore energy (capped at max). */
  addEnergy(amount: number): void {
    this.update((s) => (s.energy = Math.min(s.energyMax, s.energy + amount)));
  }

  setToddlerMode(b: boolean): void {
    this.toddlerMode = b;
    try {
      localStorage.setItem('sandyhollow.toddler', b ? '1' : '0');
    } catch {
      /* ignore */
    }
  }

  /** Record a milestone in the keepsake journal — only the first time for a given text. */
  logJournal(text: string): void {
    if (this.state.journal.some((e) => e.text === text)) return;
    this.update((s) => s.journal.push({ day: s.clock.day, text }));
  }

  storageCount(itemId: string): number {
    let n = 0;
    for (const s of this.state.storage) if (s.itemId === itemId) n += s.qty;
    return n;
  }

  /** Move items from the bag into the house chest. */
  moveToStorage(itemId: string, qty = 1): void {
    if (this.countItem(itemId) < qty) return;
    this.removeItem(itemId, qty);
    this.update((s) => {
      const slot = s.storage.find((x) => x.itemId === itemId);
      if (slot) slot.qty += qty;
      else s.storage.push({ itemId, qty });
    });
  }

  /** Move items from the chest back into the bag. */
  moveFromStorage(itemId: string, qty = 1): void {
    if (this.storageCount(itemId) < qty) return;
    this.update((s) => {
      const slot = s.storage.find((x) => x.itemId === itemId);
      if (slot) slot.qty -= qty;
      s.storage = s.storage.filter((x) => x.qty > 0);
    });
    this.addItem(itemId, qty);
  }

  removeItem(itemId: string, qty = 1): boolean {
    if (this.countItem(itemId) < qty) return false;
    this.update((s) => {
      let remaining = qty;
      for (const slot of s.inventory) {
        if (slot.itemId !== itemId) continue;
        const take = Math.min(slot.qty, remaining);
        slot.qty -= take;
        remaining -= take;
        if (remaining <= 0) break;
      }
      s.inventory = s.inventory.filter((x) => x.qty > 0);
    });
    return true;
  }
}

export const store = new GameStore();
export type { GameStore };
